// Boards and streams — shared types (docs/plans/2026-10-02-borden-ontwerp.md §3.1).

import type { SubtaskSummary } from '../types/subtasks.js'

export type QueueLane = 'inbox' | 'backlog' | 'ready'
export const QUEUE_LANES: readonly QueueLane[] = ['inbox', 'backlog', 'ready']

export type Severity = 'S1' | 'S2' | 'S3' | 'S4'
export const SEVERITIES: readonly Severity[] = ['S1', 'S2', 'S3', 'S4']

export type StreamOrderRule = 'manual' | 'severity_age' | 'priority_age'
export const STREAM_ORDER_RULES: readonly StreamOrderRule[] = ['manual', 'severity_age', 'priority_age']

/** Definition of Ready checks a stream can require before a task enters Ready. */
export type DefinitionOfReadyCheck = 'acceptance_criteria' | 'no_open_blockers' | 'severity_set'
export const DEFINITION_OF_READY_CHECKS: readonly DefinitionOfReadyCheck[] = ['acceptance_criteria', 'no_open_blockers', 'severity_set']

/** Design-token names (web: bg-<color>-soft / text-<color>); 'ink' = neutral. */
export type StreamColor = 'moss' | 'info' | 'signal' | 'danger' | 'ok' | 'ink'
export const STREAM_COLORS: readonly StreamColor[] = ['moss', 'info', 'signal', 'danger', 'ok', 'ink']

export interface StreamLanes { inbox: boolean; backlogReady: boolean }
export interface StreamExpedite { severity: 'S1'; overWip: number }   // overWip 0..3

export interface StreamDefinition {
  id: string
  projectId: string
  key: string                  // WorkflowKeySchema rules; default = the workflow key
  name: string
  color: StreamColor
  ticketPrefix: string         // ^[A-Z][A-Z0-9]{0,9}$
  /** Caption of the board's create button, e.g. "Report bug"; null = derived from the stream (see the web's newTaskLabelOf). */
  newTaskLabel: string | null
  workflowKey: string          // v1: exactly one workflow per stream (immutable)
  position: number
  lanes: StreamLanes
  order: StreamOrderRule
  wipLimit: number | null      // null = no limit
  countHumanSteps: boolean
  autoPull: boolean
  paused: boolean
  expedite: StreamExpedite | null
  weeklyBudgetCents: number | null
  definitionOfReady: DefinitionOfReadyCheck[]
  /** The workflow is no longer in the project's active policy. */
  workflowMissing: boolean
  createdAt: string
  updatedAt: string
}

/** PATCH /projects/:id/streams/:streamKey — every field optional. key and workflowKey can't change. */
export interface UpdateStreamRequest {
  name?: string
  color?: StreamColor
  ticketPrefix?: string
  newTaskLabel?: string | null
  position?: number
  lanes?: StreamLanes
  order?: StreamOrderRule
  wipLimit?: number | null
  countHumanSteps?: boolean
  autoPull?: boolean
  paused?: boolean
  expedite?: StreamExpedite | null
  weeklyBudgetCents?: number | null
  definitionOfReady?: DefinitionOfReadyCheck[]
}

export interface StreamListResponse { items: StreamDefinition[] }

export type PullStopReason = 'no_stream' | 'paused' | 'auto_pull_off' | 'budget_exhausted' | 'wip_full' | 'empty'
export interface PullResult {
  started: { taskId: string; ticketKey: string | null; status: string; blockedReason: string | null }[]
  failed: { taskId: string; ticketKey: string | null; reason: string }[]
  /** Why pulling stopped (null when it stopped because it hit the per-call cap). */
  reason: PullStopReason | null
}

export interface StreamStats {
  inFlight: number
  wipLimit: number | null
  inbox: number
  backlog: number
  ready: number
  /** Started tasks with status 'blocked' (not counted in flight). */
  blocked: number
  doneThisWeek: number
  /** Median of (completed_at - first start) over tasks completed in the last 30 days; null when none. */
  medianCycleTimeMs: number | null
  spentThisWeekCents: number
  weeklyBudgetCents: number | null
  budgetExhausted: boolean
}

export type BoardColumnKind = 'lane' | 'agent' | 'human' | 'decision' | 'done'
export interface BoardColumn {
  /** 'lane:inbox' | 'lane:backlog' | 'lane:ready' | 'state:<name>' | 'done' */
  id: string
  kind: BoardColumnKind
  lane?: QueueLane
  state?: string
  label: string
  /** 'done': the terminal states folded into it. */
  terminalStates?: { name: string; outcome: 'success' | 'failure' | 'cancelled' }[]
  /** Right of the line and counted for WIP (agent/decision always; human when countHumanSteps). */
  wipCounted: boolean
  /** Total tasks in the column (may exceed the cards sent). */
  count: number
}

export interface BoardCardRun {
  id: string
  kind: 'agent' | 'decision'
  status: string
  roleName: string | null
  agentName: string | null
  startedAt: string | null
  costCents: number
}

export interface BoardCard {
  id: string
  ticketNo: number | null
  ticketKey: string | null
  title: string
  status: string                // TaskStatus
  columnId: string
  lane: QueueLane | null
  workflowState: string | null
  rank: string | null
  /** Non-manual order: the task was dragged into place (rank set). */
  pinned: boolean
  severity: Severity | null
  priority: number
  /** Open blockers (incoming 'blocks' links from tasks not completed/cancelled/rejected), at most 5. */
  blockedBy: { taskId: string; ticketKey: string | null; title: string }[]
  blockedByCount: number
  /** Times the task entered its current state, and that state's maxRounds. */
  round: number | null
  maxRounds: number | null
  pullRequestUrl: string | null
  /** When the task entered its current column (lane_entered_at, or the latest transition into its state). */
  enteredColumnAt: string
  /** Backlog/Ready cards: the stream's Definition of Ready. Null elsewhere. */
  readiness: { passed: boolean; failed: DefinitionOfReadyCheck[] } | null
  queueReason: string | null
  activeRun: BoardCardRun | null
  /** status 'awaiting_approval': a person's turn. */
  waitingOnHuman: boolean
  /** 'done' column: which terminal state. */
  outcome: { state: string; outcome: 'success' | 'failure' | 'cancelled' } | null
  /** Subtask counts for a task with subtasks; null for any other task. */
  subtaskSummary: SubtaskSummary | null
  createdAt: string
  updatedAt: string
}

export interface BoardResponse {
  stream: StreamDefinition
  columns: BoardColumn[]
  cards: BoardCard[]
  stats: StreamStats
  /** Why nothing is pulled right now; null when pulling could happen. */
  pullBlockedReason: 'paused' | 'budget_exhausted' | 'wip_full' | 'auto_pull_off' | null
}

export interface ProjectOverviewStream {
  stream: StreamDefinition
  stats: StreamStats
  pullBlockedReason: BoardResponse['pullBlockedReason']
  /** Open tasks: queued + in flight + blocked (not done). */
  openCount: number
}

export interface ProjectOverviewResponse {
  project: { id: string; key: string; name: string }
  streams: ProjectOverviewStream[]
  waitingOnYou: {
    approvalId: string
    type: string               // approvals.action_type
    taskId: string
    ticketKey: string | null
    title: string
    state: string | null
    streamKey: string | null
    since: string
  }[]
  runningNow: {
    runId: string
    taskId: string
    ticketKey: string | null
    title: string
    kind: 'agent' | 'decision'
    roleName: string | null
    agentName: string | null
    startedAt: string | null
    costCents: number
  }[]
}

/** POST /tasks/:id/queue */
export interface QueueMoveRequest {
  lane: QueueLane
  /** The card that ends up directly above the moved task (same lane), or null/omitted for the top. */
  afterTaskId?: string | null
  /** The card that ends up directly below the moved task (same lane), or null/omitted for the bottom. */
  beforeTaskId?: string | null
  /** Enter Ready even when the Definition of Ready fails. */
  force?: boolean
  /** Non-manual order: clear the task's pin (rank = null). */
  unpin?: boolean
}

/** POST /tasks/:id/submit (body optional) */
export interface SubmitTaskRequest { overrideWip?: boolean }

/** POST /tasks/:id/move-stream */
export interface MoveStreamRequest { workflowKey: string; reason?: string | null }

export type TaskLinkKind = 'blocks' | 'relates' | 'duplicate_of' | 'spawned_from'
export const TASK_LINK_KINDS: readonly TaskLinkKind[] = ['blocks', 'relates', 'duplicate_of', 'spawned_from']

export interface TaskLinkTarget {
  id: string
  projectId: string | null
  ticketKey: string | null
  title: string
  status: string
  workflowState: string | null
}
export interface TaskLinkView {
  kind: TaskLinkKind
  /** outgoing: this task → task (this task blocks / relates to / duplicates / was spawned from it). */
  direction: 'outgoing' | 'incoming'
  task: TaskLinkTarget
  createdAt: string
}
export interface TaskLinksResponse { links: TaskLinkView[] }
/** POST /tasks/:id/links — this task is the link's "from". */
export interface CreateTaskLinkRequest { kind: TaskLinkKind; taskId: string }

/** Error codes (ApiError.code) of the new endpoints. */
export const WIP_LIMIT_REACHED_CODE = 'wip_limit_reached'           // 409, details: WipLimitReachedDetails
export const DEFINITION_OF_READY_FAILED_CODE = 'definition_of_ready_failed' // 422, details: { failed: DefinitionOfReadyCheck[] }
export const LINK_CYCLE_CODE = 'link_cycle'                          // 422
export interface WipLimitReachedDetails { streamKey: string; inFlight: number; wipLimit: number }
